import ApiService from './apiService';

export type ShiftStatus = 'scheduled' | 'cancelled' | 'completed';
export type ShiftRequestType = 'edit' | 'swap';
export type ShiftRequestStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';

export interface ShiftUser {
  id: string;
  username?: string;
  email?: string;
  name?: string;
}

export interface ShiftDepartment {
  id: string;
  name: string;
}

export interface Shift {
  id: string;
  organizationId: string;
  departmentId: string;
  employeeId: string;
  startAt: string;
  endAt: string;
  roleLabel?: string;
  location?: string;
  notes?: string;
  status: ShiftStatus;
  department?: ShiftDepartment;
  employee?: ShiftUser;
}

export interface ShiftChangeRequest {
  id: string;
  shiftId: string;
  requesterId: string;
  targetEmployeeId?: string;
  type: ShiftRequestType;
  proposedStartAt?: string;
  proposedEndAt?: string;
  reason?: string;
  status: ShiftRequestStatus;
  reviewNote?: string;
  createdAt: string;
  shift: Shift;
  requester?: ShiftUser;
  targetEmployee?: ShiftUser;
}

export interface ShiftNotification {
  id: string;
  title: string;
  message: string;
  readAt?: string;
  createdAt: string;
  requestId?: string;
}

export const ShiftsService = {
  getShifts: (params: { weekStart: string; departmentId?: string; employeeId?: string }) =>
    ApiService.get<Shift[]>('/shifts', { params }),

  createShift: (data: {
    departmentId: string;
    employeeId: string;
    startAt: string;
    endAt: string;
    roleLabel?: string;
    location?: string;
    notes?: string;
  }) => ApiService.post<Shift>('/shifts', data),

  updateShift: (id: string, data: Partial<Shift>) =>
    ApiService.put<Shift>(`/shifts/${id}`, data),

  cancelShift: (id: string) =>
    ApiService.delete<Shift>(`/shifts/${id}`),

  createRequest: (shiftId: string, data: {
    type: ShiftRequestType;
    targetEmployeeId?: string;
    proposedStartAt?: string;
    proposedEndAt?: string;
    reason?: string;
  }) => ApiService.post<ShiftChangeRequest>(`/shifts/${shiftId}/requests`, data),

  getRequests: (status?: ShiftRequestStatus) =>
    ApiService.get<ShiftChangeRequest[]>('/shifts/requests', {
      params: status ? { status } : undefined,
    }),

  approveRequest: (id: string, reviewNote?: string) =>
    ApiService.put<ShiftChangeRequest>(`/shifts/requests/${id}/approve`, { reviewNote }),

  rejectRequest: (id: string, reviewNote?: string) =>
    ApiService.put<ShiftChangeRequest>(`/shifts/requests/${id}/reject`, { reviewNote }),

  getNotifications: () =>
    ApiService.get<ShiftNotification[]>('/shifts/notifications'),

  markNotificationRead: (id: string) =>
    ApiService.put<ShiftNotification>(`/shifts/notifications/${id}/read`, {}),
};

export default ShiftsService;
